import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ClientAuthProvider } from "./contexts/ClientAuthContext";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import AppLayout from "./components/shared/AppLayout";
import InterventionsLayout from "./components/interventions/InterventionsLayout";
const InterventionsTicketsPage = lazy(() => import("./pages/app/InterventionsTicketsPage"));
const InterventionsTicketDetailPage = lazy(() => import("./pages/app/InterventionsTicketDetailPage"));
const InterventionsPlanningPage = lazy(() => import("./pages/app/InterventionsPlanningPage"));
const InterventionsParcPage = lazy(() => import("./pages/app/InterventionsParcPage"));
const InterventionsContratsPage = lazy(() => import("./pages/app/InterventionsContratsPage"));
const InterventionsHomePage = lazy(() => import("./pages/app/InterventionsHomePage"));
import { UNIVERSES_ENABLED } from "./lib/universes";
import CookieConsent from "./components/shared/CookieConsent";
import InstallPrompt from "./components/shared/InstallPrompt";
const AccountSettings = lazy(() => import("./pages/app/AccountSettings"));
const HelpPlans = lazy(() => import("./pages/app/HelpPlans"));
const HelpRoles = lazy(() => import("./pages/app/HelpRoles"));
const AdminCompanyDetail = lazy(() => import("./pages/admin/AdminCompanyDetail"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminSessions = lazy(() => import("./pages/admin/AdminSessions"));
import ProtectedRoute from "./components/shared/ProtectedRoute";
import ClientRoute from "./components/shared/ClientRoute";
const Landing = lazy(() => import("./pages/public/Landing"));
const Login = lazy(() => import("./pages/public/Login"));
const Register = lazy(() => import("./pages/public/Register"));
const ForgotPassword = lazy(() => import("./pages/public/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/public/ResetPassword"));
const AcceptInvitation = lazy(() => import("./pages/public/AcceptInvitation"));
const VerifyEmail = lazy(() => import("./pages/public/VerifyEmail"));
const Dashboard = lazy(() => import("./pages/app/Dashboard"));
// DashboardOwner est utilisé via Dashboard.jsx (aiguillage par rôle).
// L'import direct n'est plus nécessaire car la route /dashboard-v2 est
// désormais un Navigate vers /dashboard.
const Placeholder = lazy(() => import("./pages/app/Placeholder"));
const UsersList = lazy(() => import("./pages/app/UsersList"));
const InterimsPunchPage = lazy(() => import("./pages/app/InterimsPunchPage"));
const UserDetail = lazy(() => import("./pages/app/UserDetail"));
const SitesList = lazy(() => import("./pages/app/SitesList"));
const SitesMap = lazy(() => import("./pages/app/SitesMap"));
const SiteForm = lazy(() => import("./pages/app/SiteForm"));
const SiteDetail = lazy(() => import("./pages/app/SiteDetail"));
const Planning = lazy(() => import("./pages/app/Planning"));
const MySchedule = lazy(() => import("./pages/app/MySchedule"));
const MySites = lazy(() => import("./pages/app/MySites"));
const MemosList = lazy(() => import("./pages/app/MemosList"));
const Timesheets = lazy(() => import("./pages/app/Timesheets"));
const Absences = lazy(() => import("./pages/app/Absences"));
const PayrollVariables = lazy(() => import("./pages/app/PayrollVariables"));
const Notifications = lazy(() => import("./pages/app/Notifications"));
const SettingsNotifications = lazy(() => import("./pages/app/SettingsNotifications"));
const SettingsMetiers = lazy(() => import("./pages/app/SettingsMetiers"));
const Onboarding = lazy(() => import("./pages/app/Onboarding"));
const Welcome = lazy(() => import("./pages/app/Welcome"));
const SitePunch = lazy(() => import("./pages/app/SitePunch"));
const SettingsRgpd = lazy(() => import("./pages/app/SettingsRgpd"));
const SettingsCompany = lazy(() => import("./pages/app/SettingsCompany"));
const SettingsBilling = lazy(() => import("./pages/app/SettingsBilling"));
const SettingsIntegrations = lazy(() => import("./pages/app/SettingsIntegrations"));
const PricingPage = lazy(() => import("./pages/public/Pricing"));
const StubCheckout = lazy(() => import("./pages/public/StubCheckout"));
const StubPortal = lazy(() => import("./pages/public/StubPortal"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const CookiesPage = lazy(() => import("./pages/legal/CookiesPage"));
const LegalNoticePage = lazy(() => import("./pages/legal/LegalNoticePage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminCompanies = lazy(() => import("./pages/admin/AdminCompanies"));
const NotFound = lazy(() => import("./pages/NotFound"));
import { ProductTourProvider } from "./contexts/ProductTourContext";
const Subcontractors = lazy(() => import("./pages/app/Subcontractors"));
// V5-T4 : Reporting
const Reports = lazy(() => import("./pages/app/Reports"));
// V5-T9 : Espace client
const ClientLogin = lazy(() => import("./pages/client/ClientLogin"));
const ClientAcceptInvite = lazy(() => import("./pages/client/ClientAcceptInvite"));
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const ClientSiteView = lazy(() => import("./pages/client/ClientSiteView"));
// Phase 3.D : page publique QR maintenance (sans auth)
const PublicMaintenancePage = lazy(() => import("./pages/public/PublicMaintenancePage"));
// Phase D : QR public form 4 étapes + tracking (sans auth)
const PublicMaintenanceForm = lazy(() => import("./pages/public/PublicMaintenanceForm"));
const PublicMaintenanceTracking = lazy(() => import("./pages/public/PublicMaintenanceTracking"));
const PortailSuivi = lazy(() => import("./pages/public/PortailSuivi"));
// Demandes : matériel / outils / EPI / autres
const DemandesList = lazy(() => import("./pages/app/DemandesList"));
const ExpensesPage = lazy(() => import("./pages/app/ExpensesPage"));
const LaborCostPage = lazy(() => import("./pages/app/LaborCostPage"));
const MilestonesPage = lazy(() => import("./pages/app/MilestonesPage"));
// Étape 1 : Espace Maintenance autonome (chantiers-maintenance séparés)
const MaintenanceSpacePage = lazy(() => import("./pages/app/MaintenanceSpacePage"));
const RHPage = lazy(() => import("./pages/app/RHPage"));
import ModuleRoute from "./components/shared/ModuleRoute";
const DemandeForm = lazy(() => import("./pages/app/DemandeForm"));
const DemandeDetail = lazy(() => import("./pages/app/DemandeDetail"));
const DemandesDashboard = lazy(() => import("./pages/app/DemandesDashboard"));
const DemandesByType = lazy(() => import("./pages/app/DemandesByType"));
// Rapports d'intervention : liste + détail + création (via wizard existant)
const RapportsList = lazy(() => import("./pages/app/RapportsList"));
const RapportDetail = lazy(() => import("./pages/app/RapportDetail"));
// Tâche chantier : page détail fullscreen + page liste V8 + création 4 étapes
const TaskDetail = lazy(() => import("./pages/app/TaskDetail"));
const TasksPage = lazy(() => import("./pages/app/TasksPage"));
const TaskCreatePage = lazy(() => import("./pages/app/TaskCreatePage"));
const TasksDashboardPage = lazy(() => import("./pages/app/TasksDashboardPage"));
const MyTasks = lazy(() => import("./pages/app/MyTasks"));
const SearchResults = lazy(() => import("./pages/app/SearchResults"));
// BTP-Suivi · Espace Sous-traitant (PR-B4 MVP — home page)
const SousTraitantHome = lazy(() => import("./pages/app/SousTraitantHome"));
// feat/soustraitant-admin-frontend : pages admin owner
const SousTraitantsAdminList = lazy(() => import("./pages/app/admin/SousTraitantsAdminList"));
const SousTraitantsAdminDetail = lazy(() => import("./pages/app/admin/SousTraitantsAdminDetail"));
const FacturesValidationPage = lazy(() => import("./pages/app/admin/FacturesValidationPage"));
// BTP-Suivi · Espace Sous-traitant — tabs (feat/soustraitant-frontend-tabs)
import SousTraitantLayout from "./components/soustraitant/SousTraitantLayout";
const SousTraitantChantiers = lazy(() => import("./pages/app/SousTraitantChantiers"));
const SousTraitantChantierDetail = lazy(() => import("./pages/app/SousTraitantChantierDetail"));
const SousTraitantTaches = lazy(() => import("./pages/app/SousTraitantTaches"));
const SousTraitantFinances = lazy(() => import("./pages/app/SousTraitantFinances"));
const SousTraitantProfil = lazy(() => import("./pages/app/SousTraitantProfil"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Par défaut, react-query MET EN PAUSE toute requête dès qu'il estime le
      // navigateur hors ligne : la requête reste « pending » indéfiniment, sans
      // jamais passer en erreur. Les écrans affichent alors un état vide
      // permanent — « Aucune tâche assignée » — sans message ni bouton de
      // réessai, et c'est le comportement observé même avec navigator.onLine
      // à true. L'application n'ayant aucun cache hors ligne, la mise en pause
      // n'apporte rien : on tente, et on échoue proprement.
      networkMode: "always",
    },
    mutations: { networkMode: "always" },
  },
});

const SITES_ROLES          = ["owner", "manager", "foreman"];
const MANAGER_ROLES        = ["owner", "manager"];
const WORKER_ROLES         = ["worker"];
const WORKER_FOREMAN_ROLES  = ["worker", "foreman"];

// Univers Interventions (PLAN_SEPARATION_UNIVERS.md, flag off par défaut).
// Owner/manager sont redirigés des anciennes routes maintenance vers le
// nouvel espace ; foreman/worker gardent les pages historiques (pas de
// sélecteur pour eux : leurs affectations font foi).
function UniverseRedirect({ to, children }) {
  const { user } = useAuth();
  const params = useParams();
  if (UNIVERSES_ENABLED && user && MANAGER_ROLES.includes(user.role)) {
    const target = Object.entries(params).reduce(
      (acc, [k, v]) => acc.replace(`:${k}`, v),
      to,
    );
    return <Navigate to={target} replace />;
  }
  return children;
}

function LogoutRedirect() {
  const { logout } = useAuth();
  React.useEffect(() => {
    logout().finally(() => window.location.replace("/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// B2 — ferme tous les toasts au changement de route (sinon une erreur de
// validation/géocodage « suit » l'utilisateur de page en page).
function ToastRouteCleaner() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    toast.dismiss();
  }, [pathname]);
  return null;
}

/* BUG-024 — les 100 pages de l'application etaient importees statiquement ici,
   donc toutes presentes dans le paquet initial : 4,29 Mo a analyser et 1,33 Mo
   a telecharger avant le premier ecran utilisable, sur des telephones de
   chantier en 4G partagee. Le decoupage de code existait mais ne portait
   presque rien (7 fragments de ~30 ko pour un main de 4,29 Mo).

   Chaque page est desormais chargee a la demande. Ce qui reste immediat, c'est
   l'ossature commune a toutes les routes : contextes, gardes de route,
   gabarits, frontiere d'erreur. */
function EcranDeChargement() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" role="status" aria-live="polite">
      <span className="sr-only">Chargement…</span>
      <span className="h-6 w-6 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" aria-hidden="true" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ClientAuthProvider>
            <BrowserRouter>
              <ProductTourProvider>
                <ToastRouteCleaner />
                <Suspense fallback={<EcranDeChargement />}>
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/signup" element={<Navigate to="/register" replace />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/logout" element={<LogoutRedirect />} />
                  <Route path="/punch" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />

                  {/* Legal */}
                  <Route path="/legal/terms"         element={<TermsPage />} />
                  <Route path="/legal/privacy"       element={<PrivacyPage />} />
                  <Route path="/legal/cookies"       element={<CookiesPage />} />
                  <Route path="/legal/legal-notice"  element={<LegalNoticePage />} />

                  {/* Billing */}
                  <Route path="/pricing"             element={<PricingPage />} />
                  <Route path="/billing/stub-checkout" element={<ProtectedRoute><StubCheckout /></ProtectedRoute>} />
                  <Route path="/billing/stub-portal"   element={<ProtectedRoute><StubPortal /></ProtectedRoute>} />

                  {/* Super-admin */}
                  <Route path="/admin/login"         element={<AdminLogin />} />
                  <Route path="/admin"               element={<AdminHome />} />
                  <Route path="/admin/companies"     element={<AdminCompanies />} />
                  <Route path="/admin/companies/:id" element={<AdminCompanyDetail />} />
                  <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
                  <Route path="/admin/audit"         element={<AdminAudit />} />
                  <Route path="/admin/sessions"      element={<AdminSessions />} />

                  {/* Punch bare layout */}
                  <Route path="/sites/:siteId/punch" element={<ProtectedRoute><SitePunch /></ProtectedRoute>} />

                  {/* Onboarding */}
                  <Route path="/onboarding" element={<ProtectedRoute allowedRoles={["owner"]}><Onboarding /></ProtectedRoute>} />
                  <Route path="/welcome"    element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

                  {/* Phase 3.D : QR public maintenance (sans auth, sans AppLayout) */}
                  <Route path="/m/:token" element={<PublicMaintenancePage />} />

                  {/* Phase D : QR public form 4 étapes + tracking (sans auth) */}
                  <Route path="/public/maintenance/:chantier_token" element={<PublicMaintenanceForm />} />
                  <Route path="/public/maintenance/track/:tracking_token" element={<PublicMaintenanceTracking />} />
                  {/* Portail « mes demandes » du décideur (maquette P4, 16/08) */}
                  <Route path="/suivi/:contactToken" element={<PortailSuivi />} />

                  {/* ---- V5-T9 : Espace client ---- */}
                  <Route path="/client/login"          element={<ClientLogin />} />
                  <Route path="/client/accept-invite"  element={<ClientAcceptInvite />} />
                  <Route path="/client/dashboard"      element={<ClientRoute><ClientDashboard /></ClientRoute>} />
                  <Route path="/client/sites/:siteId"  element={<ClientRoute><ClientSiteView /></ClientRoute>} />
                  <Route path="/client"                element={<Navigate to="/client/dashboard" replace />} />

                  {/* Univers Interventions (GMAO) — shell dédié HORS AppLayout,
                      owner/manager uniquement, derrière REACT_APP_FEATURE_UNIVERSES.
                      Réutilise les pages maintenance existantes (PLAN_SEPARATION_UNIVERS.md). */}
                  {UNIVERSES_ENABLED && (
                    <Route element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><InterventionsLayout /></ProtectedRoute>}>
                      <Route path="/interventions" element={<ModuleRoute module="module_maintenance"><InterventionsHomePage /></ModuleRoute>} />
                      <Route path="/interventions/lieux" element={<ModuleRoute module="module_maintenance"><MaintenanceSpacePage /></ModuleRoute>} />
                      <Route path="/interventions/lieux/:id" element={<ModuleRoute module="module_maintenance"><MaintenanceSpacePage /></ModuleRoute>} />
                      <Route path="/interventions/tickets" element={<ModuleRoute module="module_maintenance"><InterventionsTicketsPage /></ModuleRoute>} />
                      <Route path="/interventions/tickets/:siteId/:code" element={<ModuleRoute module="module_maintenance"><InterventionsTicketDetailPage /></ModuleRoute>} />
                      <Route path="/interventions/planning" element={<ModuleRoute module="module_maintenance"><InterventionsPlanningPage /></ModuleRoute>} />
                      <Route path="/interventions/parc" element={<ModuleRoute module="module_maintenance"><InterventionsParcPage /></ModuleRoute>} />
                      <Route path="/interventions/contrats" element={<ModuleRoute module="module_maintenance"><InterventionsContratsPage /></ModuleRoute>} />
                      <Route path="/interventions/rapports" element={<ModuleRoute module="module_maintenance"><RapportsList /></ModuleRoute>} />
                      <Route path="/interventions/rapports/:id" element={<RapportDetail />} />
                    </Route>
                  )}

                  {/* Protected app */}
                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    {/* BTP-Suivi : /dashboard aiguillage owner/manager → DashboardOwner
                        (cf. Dashboard.jsx). /dashboard-v2 conservé en redirect vers
                        /dashboard pour les liens existants (bannière, bookmarks). */}
                    <Route path="/dashboard"         element={<Dashboard />} />
                    <Route path="/dashboard-v2"      element={<Navigate to="/dashboard" replace />} />
                    <Route path="/sites"             element={<ProtectedRoute allowedRoles={["owner","manager","foreman","worker"]}><SitesList /></ProtectedRoute>} />
                    <Route path="/sites/new"         element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><SiteForm mode="create" /></ProtectedRoute>} />
                    <Route path="/sites/map"         element={<ProtectedRoute allowedRoles={SITES_ROLES}><SitesMap /></ProtectedRoute>} />
                    <Route path="/sites/:id"         element={<SiteDetail />} />
                    <Route path="/sites/:id/edit"   element={<ProtectedRoute allowedRoles={SITES_ROLES}><SiteForm mode="edit" /></ProtectedRoute>} />
                    <Route path="/my-sites"          element={<ProtectedRoute allowedRoles={WORKER_FOREMAN_ROLES}><MySites /></ProtectedRoute>} />
                    <Route path="/planning"          element={<ModuleRoute module="module_planning"><ProtectedRoute allowedRoles={SITES_ROLES}><Planning /></ProtectedRoute></ModuleRoute>} />
                    {/* PROD-003 : le chef de chantier est affectable au planning comme
                        l'ouvrier — la notification « assignment.created » pointe ici, et
                        lui repondait 403. */}
                    <Route path="/my-schedule"       element={<ModuleRoute module="module_planning"><ProtectedRoute allowedRoles={["worker", "foreman"]}><MySchedule /></ProtectedRoute></ModuleRoute>} />
                    <Route path="/memos"             element={<ProtectedRoute allowedRoles={SITES_ROLES}><MemosList /></ProtectedRoute>} />
                    <Route path="/timesheets"        element={<ModuleRoute module="module_pointage"><Timesheets /></ModuleRoute>} />
                    <Route path="/absences"          element={<ModuleRoute module="module_rh"><Absences /></ModuleRoute>} />
                    <Route path="/users"             element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><UsersList /></ProtectedRoute>} />
                    <Route path="/interimaires"      element={<ModuleRoute module="module_pointage"><ProtectedRoute allowedRoles={SITES_ROLES}><InterimsPunchPage /></ProtectedRoute></ModuleRoute>} />
                    <Route path="/users/:id"         element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><UserDetail /></ProtectedRoute>} />
                    <Route path="/notifications"     element={<Notifications />} />
                    <Route path="/settings/notifications" element={<SettingsNotifications />} />
                    <Route path="/settings/metiers" element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><SettingsMetiers /></ProtectedRoute>} />
                    <Route path="/settings/billing"       element={<ProtectedRoute allowedRoles={["owner"]}><SettingsBilling /></ProtectedRoute>} />
                    <Route path="/settings/payroll-variables" element={<ProtectedRoute allowedRoles={["owner"]}><PayrollVariables /></ProtectedRoute>} />
                    {/* Stores : la suppression de compte doit être accessible à tout utilisateur (Apple/Google) */}
                    <Route path="/settings/rgpd"      element={<ProtectedRoute allowedRoles={["owner","manager","foreman","worker"]}><SettingsRgpd /></ProtectedRoute>} />
                    <Route path="/settings/company"   element={<ProtectedRoute allowedRoles={["owner"]}><SettingsCompany /></ProtectedRoute>} />
                    <Route path="/settings/integrations" element={<ProtectedRoute allowedRoles={["owner"]}><SettingsIntegrations /></ProtectedRoute>} />
                    {/* Import CSV retire le 16/08/2026 : 11 intervenants en
                        base, tous crees a la main. Un import de masse sert a
                        partir de plusieurs dizaines de lignes. */}
                    <Route path="/subcontractors"    element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><Subcontractors /></ProtectedRoute>} />
                    {/* feat/soustraitant-admin-frontend : gestion ST + validation factures */}
                    <Route path="/admin/soustraitants"     element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><SousTraitantsAdminList /></ProtectedRoute>} />
                    <Route path="/admin/soustraitants/:id" element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><SousTraitantsAdminDetail /></ProtectedRoute>} />
                    <Route path="/admin/factures"          element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><FacturesValidationPage /></ProtectedRoute>} />
                    {/* V5-T4 : Reporting */}
                    <Route path="/reports"           element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><Reports /></ProtectedRoute>} />
                    {/* Rapports d'intervention */}
                    <Route path="/rapports-intervention"     element={<UniverseRedirect to="/interventions/rapports"><ModuleRoute module="module_maintenance"><RapportsList /></ModuleRoute></UniverseRedirect>} />
                    <Route path="/rapports-intervention/:id" element={<UniverseRedirect to="/interventions/rapports/:id"><RapportDetail /></UniverseRedirect>} />
                    {/* Tâche chantier — page liste V8 + création + détail fullscreen */}
                    <Route path="/tasks" element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><TasksDashboardPage /></ProtectedRoute>} />
                    <Route path="/my-tasks" element={<MyTasks />} />
                    <Route path="/search" element={<SearchResults />} />
                    <Route path="/sites/:siteId/tasks" element={<TasksPage />} />
                    <Route path="/sites/:siteId/tasks/new" element={<TaskCreatePage />} />
                    <Route path="/sites/:siteId/tasks/:taskId" element={<TaskDetail />} />
                    {/* BTP-Suivi · Espace Sous-traitant — feat/soustraitant-frontend-tabs
                        Layout commun SousTraitantLayout (header + bottom nav) avec Outlet.
                        Toutes les sous-routes sont protégées par rôle "soustraitant". */}
                    <Route
                      element={
                        <ProtectedRoute allowedRoles={["soustraitant"]}>
                          <SousTraitantLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="/sous-traitant" element={<SousTraitantHome />} />
                      <Route path="/sous-traitant/accueil" element={<SousTraitantHome />} />
                      <Route path="/sous-traitant/chantiers" element={<SousTraitantChantiers />} />
                      <Route path="/sous-traitant/chantiers/:id" element={<SousTraitantChantierDetail />} />
                      <Route path="/sous-traitant/taches" element={<SousTraitantTaches />} />
                      <Route path="/sous-traitant/finances" element={<SousTraitantFinances />} />
                      <Route path="/sous-traitant/profil" element={<SousTraitantProfil />} />
                    </Route>
                    {/* Demandes : worker/foreman/manager créent, owner valide */}
                    <Route path="/demandes"                  element={<DemandesList />} />
                    <Route path="/demandes/dashboard"        element={<ProtectedRoute allowedRoles={["owner","manager"]}><DemandesDashboard /></ProtectedRoute>} />
                    <Route path="/demandes/nouvelle"         element={<DemandeForm />} />
                    <Route path="/demandes/type/:type"       element={<DemandesByType />} />
                    <Route path="/demandes/:id"              element={<DemandeDetail />} />
                    {/* Module Stock retire de l'application le 16/08/2026.
                        L'API /stock reste en place et les donnees intactes :
                        le module reviendra sous une autre forme. */}
                    <Route path="/notes-de-frais" element={<ModuleRoute module="module_rh"><ExpensesPage /></ModuleRoute>} />
                    <Route path="/couts-mo" element={<ModuleRoute module="module_pointage"><ProtectedRoute allowedRoles={["owner","manager"]}><LaborCostPage /></ProtectedRoute></ModuleRoute>} />
                    {/* Jalons : sans garde de rôle, un ouvrier atteignait la page par l'URL directe */}
                    <Route path="/jalons" element={<ModuleRoute module="module_planning"><ProtectedRoute allowedRoles={SITES_ROLES}><MilestonesPage /></ProtectedRoute></ModuleRoute>} />
                    {/* Gammes de maintenance retirees le 16/08/2026 : inutiles
                        a ce stade. La page et l'API restent en place, seules les
                        routes et l'entree de menu disparaissent. */}
                    {/* Espace Maintenance : liste + détail d'un chantier-maintenance */}
                    <Route path="/maintenance" element={<UniverseRedirect to="/interventions/lieux"><ModuleRoute module="module_maintenance"><ProtectedRoute allowedRoles={SITES_ROLES}><MaintenanceSpacePage /></ProtectedRoute></ModuleRoute></UniverseRedirect>} />
                    <Route path="/maintenance/:id" element={<UniverseRedirect to="/interventions/lieux/:id"><ModuleRoute module="module_maintenance"><ProtectedRoute allowedRoles={SITES_ROLES}><MaintenanceSpacePage /></ProtectedRoute></ModuleRoute></UniverseRedirect>} />
                    <Route path="/rh" element={<ModuleRoute module="module_rh"><ProtectedRoute allowedRoles={["owner","manager"]}><RHPage /></ProtectedRoute></ModuleRoute>} />
                    <Route path="/profile"            element={<Navigate to="/settings/account" replace />} />
                    <Route path="/settings/account"   element={<AccountSettings />} />
                    <Route path="/help/plans"          element={<HelpPlans />} />
                    <Route path="/help/roles"          element={<HelpRoles />} />
                    <Route path="/help"               element={<Navigate to="/help/roles" replace />} />
                    <Route path="/settings/profile"   element={<Navigate to="/settings/account" replace />} />
                    <Route path="/settings"           element={<Navigate to="/settings/account" replace />} />
                    <Route path="/parametres"         element={<Navigate to="/settings/account" replace />} />
                    <Route path="/parametres/notifications" element={<Navigate to="/settings/notifications" replace />} />
                    <Route path="/parametres/billing"  element={<Navigate to="/settings/billing" replace />} />
                    <Route path="/parametres/company"  element={<Navigate to="/settings/company" replace />} />
                    <Route path="/parametres/rgpd"     element={<Navigate to="/settings/rgpd" replace />} />
                    <Route path="/company/settings"   element={<Navigate to="/settings/company" replace />} />
                    <Route path="/heures"             element={<Navigate to="/timesheets" replace />} />
                    <Route path="/billing"            element={<Navigate to="/settings/billing" replace />} />
                    <Route path="/utilisateurs"       element={<Navigate to="/users" replace />} />
                    <Route path="/chantiers"          element={<Navigate to="/sites" replace />} />
                    <Route path="/planification"      element={<Navigate to="/planning" replace />} />
                    <Route path="/conges"             element={<Navigate to="/absences" replace />} />
                    <Route path="/taches"             element={<Navigate to="/tasks" replace />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
                <Toaster richColors position="bottom-right" closeButton duration={3000} />
                <CookieConsent />
                <InstallPrompt />
              </ProductTourProvider>
            </BrowserRouter>
          </ClientAuthProvider>
        </AuthProvider>
      </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
